import { useState } from "react";
import Box from "@mui/material/Box";
import BlockRounded from "@mui/icons-material/BlockRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import CloudOffRounded from "@mui/icons-material/CloudOffRounded";
import EventBusyRounded from "@mui/icons-material/EventBusyRounded";
import HistoryEduRounded from "@mui/icons-material/HistoryEduRounded";
import TimerOffRounded from "@mui/icons-material/TimerOffRounded";
import WrongLocationRounded from "@mui/icons-material/WrongLocationRounded";
import type { KioskScanResult } from "@bw/shared/types/generated";
import type { KioskDisplay } from "../hooks/useKioskController";
import {
  greenDetailLine,
  greenHeadline,
  greenNextStep,
  greenVisitDots,
  redReasonCopy,
  type RedIcon,
} from "../lib/kioskResult";
import { KIOSK, kpx } from "./kioskTheme";

const RED_ICONS: Record<RedIcon, typeof BlockRounded> = {
  block: BlockRounded,
  timer_off: TimerOffRounded,
  wrong_location: WrongLocationRounded,
  event_busy: EventBusyRounded,
  waiver: HistoryEduRounded,
};

function BackToReady({ secondsLeft, durationMs }: { secondsLeft: number; durationMs: number }) {
  return (
    <>
      <Box
        sx={{
          position: "absolute",
          bottom: kpx(22),
          left: 0,
          right: 0,
          textAlign: "center",
          fontSize: 16,
          color: KIOSK.onColor,
        }}
      >
        Back to ready in {Math.max(1, secondsLeft)} s
      </Box>
      <Box
        aria-hidden
        sx={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 8, bgcolor: KIOSK.track }}
      >
        <Box
          component="i"
          sx={{
            display: "block",
            height: "100%",
            bgcolor: KIOSK.white,
            transformOrigin: "left center",
            "@keyframes kioskDrain": { from: { transform: "scaleX(1)" }, to: { transform: "scaleX(0)" } },
            animation: `kioskDrain ${durationMs}ms linear forwards`,
          }}
        />
      </Box>
    </>
  );
}

function VisitDots({ dots }: { dots: boolean[] }) {
  return (
    <Box aria-hidden sx={{ display: "flex", gap: kpx(12), mt: kpx(14), flexWrap: "wrap", justifyContent: "center" }}>
      {dots.map((left, index) => (
        <Box
          key={index}
          component="i"
          sx={{
            width: kpx(30),
            height: kpx(30),
            borderRadius: "50%",
            boxSizing: "border-box",
            ...(left ? { bgcolor: KIOSK.white } : { border: `${kpx(3)} solid ${KIOSK.dotEmpty}` }),
          }}
        />
      ))}
    </Box>
  );
}

function Name({ firstName, tag, size }: { firstName: string | null; tag: string | null; size: number }) {
  if (!firstName && !tag) return null;
  return (
    <Box sx={{ fontSize: kpx(size), fontWeight: 700, lineHeight: 1, letterSpacing: "-1px" }}>
      {firstName ?? "Welcome"}
      {tag ? (
        <Box component="span" sx={{ opacity: 0.75, fontWeight: 500 }}>
          {" "}
          {tag}
        </Box>
      ) : null}
    </Box>
  );
}

function Portrait({ result }: { result: KioskScanResult }) {
  const initial = result.first_name?.trim().charAt(0).toUpperCase() ?? "";
  // A photo that fails to load falls back to the initial, never a broken image.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const photo = result.avatar_url && result.avatar_url !== failedUrl ? result.avatar_url : null;
  return (
    <Box sx={{ position: "relative", width: kpx(190), height: kpx(190), mb: kpx(8), flex: "none" }}>
      {photo ? (
        <Box
          component="img"
          src={photo}
          onError={() => setFailedUrl(photo)}
          alt={result.first_name ? `${result.first_name}'s profile photo` : "Profile photo"}
          sx={{
            display: "block",
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            objectFit: "cover",
            boxShadow: `0 0 0 ${kpx(8)} ${KIOSK.ring}`,
            bgcolor: KIOSK.greenDeep,
          }}
        />
      ) : (
        <Box
          aria-hidden
          sx={{
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            boxShadow: `0 0 0 ${kpx(8)} ${KIOSK.ring}`,
            bgcolor: KIOSK.greenDeep,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: kpx(76),
            fontWeight: 700,
          }}
        >
          {initial || <CheckCircleRounded sx={{ fontSize: kpx(110) }} />}
        </Box>
      )}
      <CheckCircleRounded
        aria-hidden
        sx={{
          position: "absolute",
          right: kpx(-10),
          bottom: kpx(-6),
          fontSize: kpx(64),
          color: KIOSK.white,
          bgcolor: KIOSK.green,
          borderRadius: "50%",
        }}
      />
    </Box>
  );
}

/**
 * The full-screen result (approved green and red frames): readable across the
 * room for about 2–3 s, then the kiosk is ready again. Green shows who it is
 * (first name, #last4, photo so staff can spot a borrowed phone), what was
 * unlocked and, for a pass, the plan with visits left (and dots); a booking
 * shows its court and start time in the facility's timezone. Red shows the
 * reason in plain words.
 */
export function KioskResultView({
  display,
  secondsLeft,
  durationMs,
  timeZone,
}: {
  display: KioskDisplay;
  secondsLeft: number;
  durationMs: number;
  timeZone?: string | null;
}) {
  const background =
    display.kind === "green" ? KIOSK.green : display.kind === "red" ? KIOSK.red : KIOSK.ready;

  let body;
  if (display.kind === "green") {
    const { result } = display;
    const detail = greenDetailLine(result, timeZone);
    const dots = greenVisitDots(result);
    const next = greenNextStep(result);
    body = (
      <>
        <Portrait result={result} />
        <Name firstName={result.first_name ?? null} tag={result.player_tag ?? null} size={84} />
        <Box sx={{ fontSize: kpx(38), fontWeight: 600, mt: kpx(14) }}>{greenHeadline(result)}</Box>
        {detail ? (
          <Box sx={{ fontSize: kpx(30), fontWeight: 500, opacity: 0.92, mt: kpx(6) }}>{detail}</Box>
        ) : null}
        {dots ? <VisitDots dots={dots} /> : null}
        {next ? <Box sx={{ fontSize: kpx(20), opacity: 0.8, mt: kpx(16) }}>{next}</Box> : null}
      </>
    );
  } else if (display.kind === "red") {
    const copy = redReasonCopy(display.reason);
    const Icon = RED_ICONS[copy.icon];
    body = (
      <>
        <Icon aria-hidden sx={{ fontSize: kpx(190) }} />
        <Box sx={{ fontSize: kpx(56), fontWeight: 700, mt: kpx(22), lineHeight: 1.1 }}>{copy.title}</Box>
        <Box sx={{ fontSize: kpx(30), opacity: 0.92, lineHeight: 1.3, maxWidth: kpx(640) }}>
          {copy.detail}
        </Box>
      </>
    );
  } else {
    body = (
      <>
        <CloudOffRounded aria-hidden sx={{ fontSize: kpx(150), color: KIOSK.text2 }} />
        <Box sx={{ fontSize: kpx(52), fontWeight: 700, mt: kpx(18), lineHeight: 1.1 }}>
          {display.offline ? "This kiosk is offline" : "We couldn't check that code"}
        </Box>
        <Box sx={{ fontSize: kpx(28), color: KIOSK.text2, lineHeight: 1.3, maxWidth: kpx(640) }}>
          Nothing was used. Please see the front desk.
        </Box>
      </>
    );
  }

  return (
    <Box
      role="alert"
      aria-live="assertive"
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 3,
        bgcolor: background,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: kpx(display.kind === "green" ? 10 : 12),
        p: kpx(40),
        boxSizing: "border-box",
      }}
    >
      {body}
      <BackToReady secondsLeft={secondsLeft} durationMs={durationMs} />
    </Box>
  );
}
